import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  AlertTitle,
  Backdrop,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Trans } from "react-i18next";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import EyeCloseIcon from "src/assets/icon/eye/Off";
import EyeOpenIcon from "src/assets/icon/eye/On";
import { reuseSignedInAccount } from "src/common/accountSession";
import { GetAsync } from "src/common/httpRequests";
import LoadingScreen from "src/components/LoadingScreen";
import {
  API_GATEWAY,
  EXTERNAL_IDENTITY_LOGIN,
  IS_AGENT,
} from "src/constants/settings";
import { MixpanelUser } from "src/mixpanel/MixpanelUser";
import { setExternalIdentities } from "src/redux/slices/externalIdentities";
import TwoFactorCodeInput from "src/sections/@dashboard/user/account/2FA/TwoFatorCodeInput";

import * as Yup from "yup";
import { MixpanelUnsuccess } from "../../../../src/mixpanel/mixpanelUnsuccess";
import {
  FormProvider,
  RHFCheckbox,
  RHFTextField,
} from "../../../components/hook-form";
import useAuth from "../../../hooks/useAuth";
import useIsMountedRef from "../../../hooks/useIsMountedRef";
import useLocales from "../../../hooks/useLocales";
import { useDispatch } from "../../../redux/store";
import { PATH_AUTH, PATH_DASHBOARD } from "../../../routes/paths";
import { AuthIcon } from "./AuthIcon";

type FormValuesProps = {
  email: string;
  password: string;
  remember: boolean;
  afterSubmit?: string;
  code?: string;
  step: 0 | 1;
};

export default function LoginForm({ setIsSelfRegistrationDisabled }) {
  const theme = useTheme();
  const { translate } = useLocales();
  const { mode } = theme?.palette;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { Login, LoginTwoFA } = useAuth();
  const isMountedRef = useIsMountedRef();
  const [showPassword, setShowPassword] = useState(false);
  const [isExternalIdLogin, setIsExternalIdLogin] = useState<boolean>(false);
  const [externalLoginIdentities, setExternalLoginIdentities] = useState<any>(
    [],
  );
  const [open, setOpen] = useState(false);
  const [loader, setLoader] = useState(true);

  const handleClose = () => {
    setOpen(false);
  };

  const LoginSchema = Yup.object().shape({
    email: Yup.string()
      .email(translate("Email must be a valid email address"))
      .when("afterSubmit", {
        is: (afterSubmit: string) => !!afterSubmit,
        then: Yup.string().required(translate("Email is invalid")),
      })
      .required(translate("Email is required")),

    password: Yup.string()
      .when("afterSubmit", {
        is: (afterSubmit: string) => !!afterSubmit,
        then: Yup.string().required(translate("Password is invalid")),
      })
      .required(translate("Password is required")),

    code: Yup.string().when(["step"], {
      is: (step: number) => step === 1,
      then: (schema) =>
        schema
          .required(translate("Code is required"))
          .matches(/^\d{6}$/, translate("Code must be exactly 6 digits")),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const defaultValues = {
    email: "",
    password: "",
    remember: false,
  };

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    setError,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = methods;

  const onSubmit = async (data: FormValuesProps) => {
    let { email, password, code, step } = data;
    email = email.toLowerCase();
    if (await reuseSignedInAccount(email)) {
      navigate(PATH_DASHBOARD.root, { replace: true });
      return;
    }
    try {
      let resp;
      if (step === 1 && code?.length === 6) {
        resp = await LoginTwoFA(email, password, code);
      } else {
        resp = await Login(email, password);
      }
      if (resp?.requires2FACode && step !== 1) {
        setValue("step", 1);
        return;
      }
      // bootIntercomm(email, email);
      const COMPANY_ID = localStorage.getItem("company_id");
      const PROFILE = JSON.parse(localStorage.getItem("profile") || "{}");

      MixpanelUser({
        eventName: "Login",
        eventSource: "Authentication",
        user: "Login",
        userEmail: email,
        status: "successful",
        signInMethod: "Email",
        organizationId: COMPANY_ID,
        userProfile: PROFILE,
        impersonateMode: false,
      });
    } catch (error) {
      console.log(error);
      MixpanelUnsuccess({
        eventName: "Login",
        eventSource: "Authentication",
        Useremail: email,
        status: "unsuccessful",
      });

      if (isMountedRef.current) {
        const message =
          error.Detail || (typeof error === "string" ? error : "Login failed");
        console.error(message);
        setError("afterSubmit", {
          ...error,
          message,
        });
        setError("email", {
          ...error,
          message: translate("Email or password is invalid"),
        });
        setError("password", {
          ...error,
          message: translate("Email or password is invalid"),
        });
        if (step === 1) {
          setError("code", {
            ...error,
            message: translate("Invalid 2FA code"),
          });
        }
      }
    }
  };

  useEffect(() => {
    let isMounted: boolean = true;
    const fetchExternalIdentity = async () => {
      const response = await GetAsync(
        `${API_GATEWAY}/api/userservice/ExternalIdentity`,
      );
      try {
        dispatch(setExternalIdentities(response.data.externalIdentities));
        if (isMounted) {
          setExternalLoginIdentities(response.data.externalIdentities);
          setIsSelfRegistrationDisabled(
            response.data.isSelfRegistrationDisabled,
          );
        }
        setLoader(false);
      } catch (e) {
        console.log(e.message);
      }
    };
    fetchExternalIdentity();
    return () => {
      isMounted = false;
    };
  }, []);

  const loginRedirect = (url: string, id: string, key: string): void => {
    setIsExternalIdLogin(true);
    localStorage.setItem("externalProviderId", id);
    localStorage.setItem("signUpMethod", key);
    window.location.replace(`${url}`);
    setIsExternalIdLogin(false);
  };
  const step = methods.watch("step") || 0;
  return (
    <>
      {loader ? (
        <LoadingScreen />
      ) : (
        <>
          <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={2}>
              {(!!errors.email || !!errors.password || errors.afterSubmit) && (
                <Alert
                  severity="error"
                  sx={{
                    border: `1px solid ${theme.palette.error.dark}`,
                    color: theme.palette.error.dark,
                    backgroundColor: "#ffefef",
                    // Ensure the message area allows inline flow
                    "& .MuiAlert-message": {
                      display: "block",
                    },
                  }}
                >
                  <AlertTitle>{translate("Information")}</AlertTitle>

                  <Typography
                    variant="body2"
                    component="div"
                    sx={{ display: "inline" }}
                  >
                    {errors.afterSubmit ? (
                      [
                        "org_status_tempblock_contact_suppport",
                        "Organization is temporary blocked. please contact support",
                        "org_status_tempblock_contact_support",
                      ].includes(errors.afterSubmit.message as string) ? (
                        <Trans
                          i18nKey={errors.afterSubmit.message}
                          components={{
                            supportLink: (
                              <Link
                                href="mailto:support@mydost.ai"
                                underline="hover"
                                target="_blank"
                                sx={{
                                  display: "inline",
                                  verticalAlign: "baseline",
                                }}
                              />
                            ),
                          }}
                        />
                      ) : (
                        translate(errors.afterSubmit.message)
                      )
                    ) : (
                      translate(
                        "Make sure all the required fields are correctly filled in",
                      )
                    )}
                  </Typography>
                </Alert>
              )}

              {/* Step 0: Email & Password */}
              {step === 0 && (
                <>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.label,
                        fontWeight: 500,
                      }}
                    >
                      {translate("authentication_screen.email_address") ||
                        "Email Address"}
                    </Typography>
                    <RHFTextField
                      name="email"
                      placeholder={translate("admin@mycompany.com") as string}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor: "#F8FAFC",
                          "& fieldset": { borderColor: "#E5E7EB" },
                        },
                      }}
                    />
                  </Box>

                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.label,
                        fontWeight: 500,
                      }}
                    >
                      {translate("authentication_screen.password") ||
                        "Password"}
                    </Typography>
                    <RHFTextField
                      name="password"
                      size="small"
                      type={showPassword ? "text" : "password"}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment
                            position="end"
                            sx={{ marginLeft: -5 }}
                          >
                            <IconButton
                              sx={{ zIndex: 1000 }}
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? (
                                <EyeOpenIcon
                                  customColor={
                                    !!errors.password
                                      ? theme.palette.error.dark
                                      : theme.palette.primary.main
                                  }
                                />
                              ) : (
                                <EyeCloseIcon
                                  customColor={
                                    !!errors.password
                                      ? theme.palette.error.dark
                                      : theme.palette.primary.main
                                  }
                                />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </>
              )}

              {/* Step 1: 2FA */}
              {step === 1 && (
                <>
                  <Typography variant="subtitle1">
                    {translate("authentication_screen.enter_2fa")}
                  </Typography>

                  <TwoFactorCodeInput
                    onChange={(value) => setValue("code", value)}
                    error={errors.code?.message}
                  />

                  <LoadingButton
                    fullWidth
                    variant="contained"
                    type="submit"
                    loading={isSubmitting}
                    disabled={isExternalIdLogin}
                    sx={{ mb: 2 }}
                  >
                    {translate("authentication_screen.verify")}
                  </LoadingButton>
                </>
              )}

              {/* Shared below both steps */}
              {step === 0 && (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ my: 2 }}
                >
                  <RHFCheckbox
                    name="remember"
                    label={
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: "14px",
                        }}
                      >
                        {translate("authentication_screen.remember_me")}
                      </Typography>
                    }
                  />

                  {!IS_AGENT && (
                    <Link
                      component={RouterLink}
                      variant="subtitle2"
                      to={PATH_AUTH.resetPassword}
                      sx={{
                        color: theme.palette.secondary.dark,
                        fontWeight: 500,
                      }}
                    >
                      {translate("authentication_screen.forgot_password")}
                    </Link>
                  )}
                </Stack>
              )}

              {step === 0 && (
                <LoadingButton
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained"
                  loading={isSubmitting}
                  disabled={isExternalIdLogin}
                  sx={{
                    borderRadius: "12px",
                    backgroundColor: theme.palette.text.secondary,
                    color: "#FFF",
                    fontWeight: 600,
                    textTransform: "none",
                    py: 1.5,
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: theme.palette.primary.dark || "#0E2954",
                      boxShadow: "none",
                    },
                  }}
                >
                  <span style={{ fontSize: "15px" }}>
                    {translate("authentication_screen.login_btn")}
                  </span>
                </LoadingButton>
              )}

              {step === 0 && EXTERNAL_IDENTITY_LOGIN && (
                <>
                  <Divider
                    sx={{
                      my: 3,
                      typography: "overline",
                      color: theme.palette.text.label,
                      "&::before, &::after": { borderColor: "#E5E7EB" },
                    }}
                  >
                    OR
                  </Divider>

                  {externalLoginIdentities?.map((item, index) => (
                    <LoadingButton
                      key={index}
                      className="external-login"
                      startIcon={<AuthIcon type={item.key} />}
                      fullWidth
                      sx={{
                        mb: 2,
                        backgroundColor: "#FFFFFF",
                        borderRadius: "12px",
                        textTransform: "none",
                        fontWeight: 600,
                        py: 1.2,
                        "&:hover": {
                          backgroundColor: "#F8FAFC",
                          border: "1px solid #C4CDD5",
                        },
                      }}
                      onClick={() => loginRedirect(item.url, item.id, item.key)}
                      size="large"
                      variant="outlined"
                      loading={isExternalIdLogin}
                      disabled={isSubmitting}
                    >
                      <span style={{ fontSize: "14px", textAlign: "center" }}>
                        {translate(
                          `authentication_screen.login_with_${item.key}`,
                        )}
                      </span>
                    </LoadingButton>
                  ))}
                </>
              )}
            </Stack>
          </FormProvider>

          <Backdrop
            sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={open}
            onClick={handleClose}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        </>
      )}
    </>
  );
}
